/**
 * Display names for every controlled element the three qualitative frameworks
 * can publish.
 *
 * These lists are CLOSED, and matching the server's closed vocabularies is the
 * point: if a rating ever arrives for an element that is not here, the component
 * renders the raw key rather than inventing a friendly name for something the
 * methodology does not recognise. A pretty label on an unregistered driver is
 * how an unscored element becomes indistinguishable from a scored one.
 */

import type {
  CompetitionAxisName,
  CompetitorClass,
  PestelCategoryName,
  PestelItem,
  PestelPosture,
  PorterForceName,
  PorterRating,
} from '@/lib/strategic-analysis/types';

// ---------------------------------------------------------------------------
// Competition
// ---------------------------------------------------------------------------

export const AXIS_LABELS: Record<CompetitionAxisName, string> = {
  customer_scope: 'Customer scope',
  solution_breadth: 'Solution breadth',
  delivery_intensity: 'Delivery intensity',
  economic_position: 'Economic position',
  vertical_specialization: 'Vertical specialisation',
  deployment_depth: 'Deployment & integration depth',
};

export const CLASSIFICATION_LABELS: Record<CompetitorClass, string> = {
  direct: 'Direct competitor',
  adjacent: 'Adjacent alternative',
  substitute: 'Substitute',
};

export const CLASSIFICATION_EXPLANATIONS: Record<CompetitorClass, string> = {
  direct: 'Targets substantially the same customer and job with a comparable solution.',
  adjacent: 'Overlaps part of the customer or the job, not all of it.',
  substitute: 'Solves the underlying job a different way.',
};

/**
 * What a position on the 1–5 rubric means. NOT a rank: `5` is one documented
 * end of an axis, not "better". Labelling these good/bad is how a positioning
 * map turns into a league table.
 */
export function positionLabel(position: number, lowLabel: string, highLabel: string): string {
  switch (position) {
    case 1:
      return lowLabel;
    case 2:
      return `Closer to ${lowLabel.toLowerCase()}`;
    case 3:
      return 'Mixed / intermediate';
    case 4:
      return `Closer to ${highLabel.toLowerCase()}`;
    case 5:
      return highLabel;
    default:
      return 'Unknown';
  }
}

// ---------------------------------------------------------------------------
// Porter
// ---------------------------------------------------------------------------

export const FORCE_LABELS: Record<PorterForceName, string> = {
  competitive_rivalry: 'Competitive rivalry',
  threat_of_new_entrants: 'Threat of new entrants',
  buyer_power: 'Buyer power',
  supplier_power: 'Supplier power',
  threat_of_substitutes: 'Threat of substitutes',
};

/** Bands only. There is no numeric rating in this product, by design. */
export const RATING_LABELS: Record<PorterRating, string> = {
  low: 'Low',
  moderate: 'Moderate',
  high: 'High',
  insufficient_evidence: 'Insufficient evidence',
};

export const PRESSURE_LABELS: Record<0 | 1 | 2, string> = {
  0: 'Reduces pressure',
  1: 'Mixed',
  2: 'Increases pressure',
};

export const DRIVER_LABELS: Record<string, string> = {
  competitor_concentration: 'Competitor concentration',
  market_growth: 'Market growth',
  differentiation: 'Differentiation',
  customer_switching_costs: 'Customer switching costs',
  fixed_cost_capacity_pressure: 'Fixed-cost and capacity pressure',
  exit_barriers: 'Exit barriers',
  capital_requirements: 'Capital requirements',
  regulatory_barriers: 'Regulatory barriers',
  technology_data_ip_barriers: 'Technology, data and IP barriers',
  scale_or_network_effects: 'Scale or network effects',
  distribution_access_barriers: 'Distribution and customer access',
  reputation_trust_requirements: 'Reputation and trust requirements',
  buyer_concentration: 'Buyer concentration',
  price_sensitivity: 'Price sensitivity',
  available_alternatives_and_information: 'Available alternatives and buyer information',
  buyer_switching_costs: 'Buyer switching costs',
  backward_integration_or_build: 'Ability to build in-house',
  supplier_concentration: 'Supplier concentration',
  alternative_inputs: 'Alternative inputs',
  supplier_switching_costs: 'Supplier switching costs',
  input_differentiation_criticality: 'Input differentiation and criticality',
  forward_integration: 'Supplier forward integration',
  platform_data_infrastructure_dependency: 'Platform, data and infrastructure dependency',
  substitute_availability: 'Substitute availability',
  relative_price_performance: 'Relative price-performance',
  switching_effort: 'Switching effort',
  propensity_to_change_behavior: 'Propensity to change behaviour',
  internal_manual_or_no_purchase_alternative: 'Internal, manual or no-purchase alternative',
};

/** An unregistered driver keeps its raw key, so it cannot pass as a scored one. */
export function driverLabel(driver: string): string {
  return DRIVER_LABELS[driver] ?? driver;
}

// ---------------------------------------------------------------------------
// PESTEL
// ---------------------------------------------------------------------------

export const PESTEL_CATEGORY_LABELS: Record<PestelCategoryName, string> = {
  political: 'Political',
  economic: 'Economic',
  social: 'Social',
  technological: 'Technological',
  environmental: 'Environmental',
  legal: 'Legal',
};

/** All six, always, in this order. An absent category is shown, not dropped. */
export const PESTEL_CATEGORY_ORDER: PestelCategoryName[] = [
  'political',
  'economic',
  'social',
  'technological',
  'environmental',
  'legal',
];

export const POSTURE_LABELS: Record<PestelPosture, string> = {
  favorable: 'Favourable',
  adverse: 'Adverse',
  mixed: 'Mixed',
  insufficient_evidence: 'Insufficient evidence',
};

export const DIRECTION_LABELS: Record<PestelItem['direction'], string> = {
  opportunity: 'Opportunity',
  risk: 'Risk',
  mixed: 'Mixed',
  unclear: 'Unclear',
};

export const MATERIALITY_LABELS: Record<PestelItem['materiality'], string> = {
  low: 'Low materiality',
  moderate: 'Moderate materiality',
  high: 'High materiality',
};

export const HORIZON_LABELS: Record<PestelItem['horizon'], string> = {
  current: 'Current',
  '0_to_2_years': '0–2 years',
  '3_to_5_years': '3–5 years',
};

export const POINT_KIND_LABELS: Record<'question' | 'signal_to_monitor' | 'robust_action', string> = {
  question: 'Question',
  signal_to_monitor: 'Signal to monitor',
  robust_action: 'Robust action',
};

/**
 * Freshness, per the methodology's default expiry periods.
 *
 * Political and legal evidence expires fastest because a rule that changed is a
 * different rule, not a slightly older one.
 */
const FRESHNESS_DAYS: Record<PestelCategoryName, number> = {
  political: 90,
  legal: 90,
  economic: 180,
  technological: 180,
  social: 365,
  environmental: 365,
};

export function freshnessWarning(
  category: PestelCategoryName,
  publishedAt: string | undefined,
  now: number,
): string | null {
  if (!publishedAt) return null;
  const published = Date.parse(publishedAt);
  if (Number.isNaN(published)) return null;
  const days = Math.floor((now - published) / 86_400_000);
  const limit = FRESHNESS_DAYS[category];
  if (days <= limit) return null;
  return `This source is ${days} days old. ${PESTEL_CATEGORY_LABELS[category]} evidence is treated as stale after ${limit} days.`;
}
